/**
 * FormatSettingsForm — reusable format-type-aware settings inputs.
 *
 * Extracted from ExportFormatWizardPage's FormatConfigForm (Step 2).
 * Renders ALL inputs the wizard renders for each format type — no functional
 * regression when the wizard switches to consume this component in Task 18.
 *
 * Presentational only: no API calls, no validation logic.
 * Validation lives in @capture/shared's validateFormatSettings.
 *
 * Field inventory per type (full parity with wizard):
 *   csv        — delimiter, includeHeader, lineEnding, lineItemMode, emptyValue, transactionGrain
 *   fixed_width — recordLength, padChar, headerRecord, trailerRecord, lineEnding, transactionGrain
 *   xml        — rootElement, documentElement, namespace, indentation, lineItemElement
 *   json       — prettyPrint, wrapInArray, nullHandling, numberAsString, lineItemsField
 *   x937       — immediateOriginRouting/Name, immediateDestinationRouting/Name,
 *                eceInstitutionRouting, bofdRouting, cashLetterBusinessDate,
 *                cashLetterId, contactName, contactPhone, imageDpi, bundleSizeLimit
 */

import { TRANSACTION_GRAINS, type FormatType, type TransactionGrain } from '@capture/shared';

export type FormatSettings = Record<string, unknown>;

export interface FormatSettingsFormProps {
  formatType: FormatType;
  value: FormatSettings;
  onChange: (next: FormatSettings) => void;
}

const TRANSACTION_GRAIN_LABELS: Record<TransactionGrain, string> = {
  transaction: 'Transaction (one row per transaction — enables computed columns)',
  check: 'Check (one row per check)',
  coupon: 'Coupon (one row per coupon)',
  allocation: 'Allocation (one row per allocation)',
};

/**
 * F-016 — Transaction grain (flat formats only). Writes `config.transactionGrain`,
 * which drives two things for a transactions-shape destination:
 *   1. the flattener's row grain (`transaction-flatten.ts` — one row per
 *      transaction/check/coupon/allocation), and
 *   2. at `'transaction'` grain, unlocks FormatColumnsEditor's per-column
 *      Expression editor so computed columns (e.g. `TXN_SUM(...)`) and promoted
 *      `transaction.*` columns can be authored — the runtime evaluates
 *      expressions ONLY at transaction grain.
 * Empty stores no key: document exports render one row per document, but a
 * transactions-shape destination falls back to the server default (check grain
 * — `grainOf`'s DEFAULT_TRANSACTION_GRAIN), so the empty label says both.
 * Rendered only for csv/fixed_width — the flat formats that use FormatColumnsEditor.
 */
function TransactionGrainRow({
  grain,
  onChange,
}: {
  grain: string | undefined;
  onChange: (grain: string | undefined) => void;
}) {
  return (
    <div className="rb-row">
      <label className="rb-label" htmlFor="fs-transaction-grain">Transaction grain</label>
      <select
        id="fs-transaction-grain"
        className="rb-select"
        value={grain ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">— Default (document exports: one row per document; transactions payloads: check grain) —</option>
        {TRANSACTION_GRAINS.map((g) => (
          <option key={g} value={g}>{TRANSACTION_GRAIN_LABELS[g]}</option>
        ))}
      </select>
    </div>
  );
}

export function FormatSettingsForm({ formatType, value, onChange }: FormatSettingsFormProps) {
  const set = (key: string, val: unknown) => onChange({ ...value, [key]: val });

  if (formatType === 'csv') {
    return (
      <div className="format-settings">
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-delimiter">Delimiter</label>
          <select
            id="fs-delimiter"
            className="rb-select"
            value={(value.delimiter as string) ?? ','}
            onChange={(e) => set('delimiter', e.target.value)}
          >
            <option value=",">Comma (,)</option>
            <option value={'\t'}>Tab</option>
            <option value="|">Pipe (|)</option>
            <option value=";">Semicolon (;)</option>
          </select>
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-include-header">Include Header</label>
          <input
            id="fs-include-header"
            type="checkbox"
            checked={(value.includeHeader as boolean) ?? true}
            onChange={(e) => set('includeHeader', e.target.checked)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-line-ending">Line Ending</label>
          <select
            id="fs-line-ending"
            className="rb-select"
            value={(value.lineEnding as string) ?? 'lf'}
            onChange={(e) => set('lineEnding', e.target.value)}
          >
            <option value="lf">LF (Unix)</option>
            <option value="crlf">CRLF (Windows)</option>
          </select>
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-line-item-mode">Line Items</label>
          <select
            id="fs-line-item-mode"
            className="rb-select"
            value={(value.lineItemMode as string) ?? 'exclude'}
            onChange={(e) => set('lineItemMode', e.target.value)}
          >
            <option value="exclude">Exclude</option>
            <option value="separate_rows">Separate rows</option>
            <option value="flatten">Flatten to columns</option>
          </select>
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-empty-value">Empty Value</label>
          <select
            id="fs-empty-value"
            className="rb-select"
            value={(value.emptyValue as string) ?? ''}
            onChange={(e) => set('emptyValue', e.target.value)}
          >
            <option value="">Empty string</option>
            <option value="NULL">NULL</option>
            <option value="N/A">N/A</option>
          </select>
        </div>
        <TransactionGrainRow
          grain={value.transactionGrain as string | undefined}
          onChange={(g) => set('transactionGrain', g)}
        />
      </div>
    );
  }

  if (formatType === 'fixed_width') {
    return (
      <div className="format-settings">
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-record-length">Record Length</label>
          <input
            id="fs-record-length"
            className="rb-input"
            type="number"
            value={(value.recordLength as number) ?? 500}
            onChange={(e) => set('recordLength', parseInt(e.target.value, 10))}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-pad-char">Pad Character</label>
          <input
            id="fs-pad-char"
            className="rb-input"
            value={(value.padChar as string) ?? ' '}
            maxLength={1}
            onChange={(e) => set('padChar', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-header-record">Header Record</label>
          <input
            id="fs-header-record"
            type="checkbox"
            checked={(value.headerRecord as boolean) ?? false}
            onChange={(e) => set('headerRecord', e.target.checked)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-trailer-record">Trailer Record</label>
          <input
            id="fs-trailer-record"
            type="checkbox"
            checked={(value.trailerRecord as boolean) ?? false}
            onChange={(e) => set('trailerRecord', e.target.checked)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-line-ending-fw">Line Ending</label>
          <select
            id="fs-line-ending-fw"
            className="rb-select"
            value={(value.lineEnding as string) ?? 'lf'}
            onChange={(e) => set('lineEnding', e.target.value)}
          >
            <option value="lf">LF (Unix)</option>
            <option value="crlf">CRLF (Windows)</option>
          </select>
        </div>
        <TransactionGrainRow
          grain={value.transactionGrain as string | undefined}
          onChange={(g) => set('transactionGrain', g)}
        />
      </div>
    );
  }

  if (formatType === 'xml') {
    return (
      <div className="format-settings">
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-root-element">Root Element</label>
          <input
            id="fs-root-element"
            className="rb-input"
            value={(value.rootElement as string) ?? ''}
            placeholder="e.g., InvoiceBatch"
            onChange={(e) => set('rootElement', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-document-element">Document Element</label>
          <input
            id="fs-document-element"
            className="rb-input"
            value={(value.documentElement as string) ?? ''}
            placeholder="e.g., Invoice"
            onChange={(e) => set('documentElement', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-namespace">Namespace</label>
          <input
            id="fs-namespace"
            className="rb-input"
            value={(value.namespace as string) ?? ''}
            placeholder="Optional URI"
            onChange={(e) => set('namespace', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-indentation">Indentation</label>
          <input
            id="fs-indentation"
            className="rb-input"
            type="number"
            value={(value.indentation as number) ?? 2}
            onChange={(e) => set('indentation', parseInt(e.target.value, 10))}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-line-item-element">Line Item Element</label>
          <input
            id="fs-line-item-element"
            className="rb-input"
            value={(value.lineItemElement as string) ?? 'LineItem'}
            onChange={(e) => set('lineItemElement', e.target.value)}
          />
        </div>
      </div>
    );
  }

  if (formatType === 'json') {
    return (
      <div className="format-settings">
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-pretty-print">Pretty Print</label>
          <input
            id="fs-pretty-print"
            type="checkbox"
            checked={(value.prettyPrint as boolean) ?? true}
            onChange={(e) => set('prettyPrint', e.target.checked)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-wrap-in-array">Wrap in Array</label>
          <input
            id="fs-wrap-in-array"
            type="checkbox"
            checked={(value.wrapInArray as boolean) ?? true}
            onChange={(e) => set('wrapInArray', e.target.checked)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-null-handling">Null Handling</label>
          <select
            id="fs-null-handling"
            className="rb-select"
            value={(value.nullHandling as string) ?? 'include'}
            onChange={(e) => set('nullHandling', e.target.value)}
          >
            <option value="include">Include nulls</option>
            <option value="exclude">Exclude nulls</option>
          </select>
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-number-as-string">Numbers as String</label>
          <input
            id="fs-number-as-string"
            type="checkbox"
            checked={(value.numberAsString as boolean) ?? true}
            onChange={(e) => set('numberAsString', e.target.checked)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-line-items-field">Line Items Field</label>
          <input
            id="fs-line-items-field"
            className="rb-input"
            value={(value.lineItemsField as string) ?? 'lineItems'}
            onChange={(e) => set('lineItemsField', e.target.value)}
          />
        </div>
      </div>
    );
  }

  if (formatType === 'x937') {
    return (
      <div className="format-settings">
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-immediate-origin-routing">Immediate Origin Routing</label>
          <input
            id="fs-x937-immediate-origin-routing"
            className="rb-input"
            value={(value.immediateOriginRouting as string) ?? ''}
            placeholder="9-digit ABA routing"
            onChange={(e) => set('immediateOriginRouting', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-immediate-origin-name">Immediate Origin Name</label>
          <input
            id="fs-x937-immediate-origin-name"
            className="rb-input"
            value={(value.immediateOriginName as string) ?? ''}
            onChange={(e) => set('immediateOriginName', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-immediate-destination-routing">Immediate Destination Routing</label>
          <input
            id="fs-x937-immediate-destination-routing"
            className="rb-input"
            value={(value.immediateDestinationRouting as string) ?? ''}
            placeholder="9-digit ABA routing"
            onChange={(e) => set('immediateDestinationRouting', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-immediate-destination-name">Immediate Destination Name</label>
          <input
            id="fs-x937-immediate-destination-name"
            className="rb-input"
            value={(value.immediateDestinationName as string) ?? ''}
            onChange={(e) => set('immediateDestinationName', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-ece-institution-routing">ECE Institution Routing</label>
          <input
            id="fs-x937-ece-institution-routing"
            className="rb-input"
            value={(value.eceInstitutionRouting as string) ?? ''}
            placeholder="9-digit ABA routing"
            onChange={(e) => set('eceInstitutionRouting', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-bofd-routing">BOFD Routing</label>
          <input
            id="fs-x937-bofd-routing"
            className="rb-input"
            value={(value.bofdRouting as string) ?? ''}
            placeholder="9-digit ABA routing"
            onChange={(e) => set('bofdRouting', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-cash-letter-business-date">Cash Letter Business Date</label>
          <input
            id="fs-x937-cash-letter-business-date"
            className="rb-input"
            type="date"
            value={(value.cashLetterBusinessDate as string) ?? ''}
            onChange={(e) => set('cashLetterBusinessDate', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-cash-letter-id">Cash Letter ID</label>
          <input
            id="fs-x937-cash-letter-id"
            className="rb-input"
            value={(value.cashLetterId as string) ?? ''}
            onChange={(e) => set('cashLetterId', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-contact-name">Contact Name</label>
          <input
            id="fs-x937-contact-name"
            className="rb-input"
            value={(value.contactName as string) ?? ''}
            onChange={(e) => set('contactName', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-contact-phone">Contact Phone</label>
          <input
            id="fs-x937-contact-phone"
            className="rb-input"
            value={(value.contactPhone as string) ?? ''}
            onChange={(e) => set('contactPhone', e.target.value)}
          />
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-image-dpi">Image DPI</label>
          <select
            id="fs-x937-image-dpi"
            className="rb-select"
            value={String((value.imageDpi as number) ?? 200)}
            onChange={(e) => set('imageDpi', parseInt(e.target.value, 10))}
          >
            <option value="200">200</option>
            <option value="240">240</option>
          </select>
        </div>
        <div className="rb-row">
          <label className="rb-label" htmlFor="fs-x937-bundle-size-limit">Bundle Size Limit</label>
          <input
            id="fs-x937-bundle-size-limit"
            className="rb-input"
            type="number"
            value={(value.bundleSizeLimit as number) ?? 300}
            onChange={(e) => set('bundleSizeLimit', parseInt(e.target.value, 10))}
          />
        </div>
      </div>
    );
  }

  // edi / pdf_summary — no wizard settings UI; render empty placeholder
  return (
    <div className="format-settings">
      <p className="rb-hint">No additional settings for this format type.</p>
    </div>
  );
}
